import React from "react";
import { createPortal } from "react-dom";
import {
  ActionButton,
  EmptyState,
  PanelShell,
  Pill,
} from "./ResultsPrimitives.jsx";
import ErrorCard, { ErrorGroupCard } from "./errorsCenter/ErrorCard.jsx";
import useErrorsCenterState from "../hooks/useErrorsCenterState.js";
import { FEEDBACK_VIEW_WARNINGS, FILTER_ALL, getFeedbackViewLabel } from "../utils/errorsCenterData.js";

export default function ErrorsCenterPanel({
  activeView = null,
  interactions = {},
  mountNode = null,
  onActivateView = null,
  openRequest = null,
  resultPayload = null,
  viewName = "errors",
}) {
  const {
    activeCountLabel,
    activeCountTone,
    activeFilter,
    activeFilterLabel,
    entries,
    handleReset,
    handleSelectErrorGroup,
    handleSelectEntry,
    handleToggleTechnical,
    resolvedExpandedErrorGroupIds,
    resolvedExpandedErrorIds,
    resolvedTechnicalErrorId,
    rootRef,
    visibleEntries,
    visibleRows,
  } = useErrorsCenterState({
    activeView,
    mountNode,
    onActivateView,
    openRequest,
    resultPayload,
    viewName,
  });

  if (!(mountNode instanceof HTMLElement)) {
    return null;
  }

  const panelTitle = getFeedbackViewLabel(viewName);
  const panelEyebrow = viewName === FEEDBACK_VIEW_WARNINGS ? "Centre des avertissements" : "Centre des alertes";
  const emptyStateTitle = viewName === FEEDBACK_VIEW_WARNINGS ? "Aucun avertissement actif" : "Aucune alerte active";
  const emptyStateMessage = viewName === FEEDBACK_VIEW_WARNINGS
    ? "Les points à surveiller s'affichent ici."
    : "Les blocages à corriger s'affichent ici."
;

  const resolvedExpandedErrorIdSet = new Set(resolvedExpandedErrorIds);
  const resolvedExpandedErrorGroupIdSet = new Set(resolvedExpandedErrorGroupIds);
  const chromeInteractions = interactions?.chrome || {};
  const canSuspendFromErrors = chromeInteractions.canMutatePlanning === true;

  const panel = (
    <div
      ref={rootRef}
      tabIndex={-1}
      className="w-full px-1 py-0.75 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
      data-react-errors-center="true"
    >
      <PanelShell
        eyebrow={panelEyebrow}
        title={panelTitle}
        className="grid gap-2"
        copyClassName="grid gap-0.25"
        headerClassName="gap-1.5"
        actions={
          <>
            {activeFilter !== FILTER_ALL ? (
              <ActionButton size="sm" tone="ghost" className="rounded-[4px]!" onClick={handleReset}>
                {activeFilterLabel} · Tout afficher
              </ActionButton>
            ) : null}
            <Pill tone={activeCountTone}>{activeCountLabel}</Pill>
          </>
        }
        actionsClassName="gap-1"
        bodyClassName="grid gap-1.5"
        titleClassName="text-[clamp(1.04rem,1.35vw,1.35rem)]"
      >
        {visibleEntries.length ? (
          <div className="grid gap-1">
            {visibleRows.map((row) => {
              if (row.rowType === "group") {
                return (
                  <ErrorGroupCard
                    key={row.rowId}
                    group={row}
                    canSuspendFromErrors={canSuspendFromErrors}
                    isOpen={resolvedExpandedErrorGroupIdSet.has(row.rowId)}
                    onToggle={() => {
                      handleSelectErrorGroup(row.rowId);
                    }}
                    onSuspendGroup={chromeInteractions.onSuspendErrorGroupFromError}
                  >
                    {row.entries.map((entry) => (
                      <ErrorCard
                        key={entry.errorId}
                        entry={entry}
                        canSuspendFromErrors={canSuspendFromErrors}
                        isOpen={resolvedExpandedErrorIdSet.has(entry.errorId)}
                        isTechnicalOpen={resolvedTechnicalErrorId === entry.errorId}
                        onSelect={() => {
                          handleSelectEntry(entry.errorId);
                        }}
                        onToggleTechnical={() => {
                          handleToggleTechnical(entry.errorId);
                        }}
                        onSuspendFromError={chromeInteractions.onSuspendOrderFromError}
                      />
                    ))}
                  </ErrorGroupCard>
                );
              }

              const entry = row.entry;
              return (
                <ErrorCard
                  key={entry.errorId}
                  entry={entry}
                  canSuspendFromErrors={canSuspendFromErrors}
                  isOpen={resolvedExpandedErrorIdSet.has(entry.errorId)}
                  isTechnicalOpen={resolvedTechnicalErrorId === entry.errorId}
                  onSelect={() => {
                    handleSelectEntry(entry.errorId);
                  }}
                  onToggleTechnical={() => {
                    handleToggleTechnical(entry.errorId);
                  }}
                  onSuspendFromError={chromeInteractions.onSuspendOrderFromError}
                />
              );
            })}
          </div>
        ) : (
          <EmptyState
            tone="subtle"
            title={emptyStateTitle}
            message={emptyStateMessage}
          />
        )}
      </PanelShell>
    </div>
  );

  return createPortal(panel, mountNode);
}
